import { useState, useContext } from "react";
import { useRemembered, useMemoryKey } from "../utilities/pageMemory";
import styles from "./style.module.css";
import HeaderButton from "./HeaderButton";
import { selectedHitContext } from "../GrantView/SelectedHitProvider";
import GrantList from "./grantLists";
import { SearchOnly } from "../SearchRow";

/** ===== Results column =====
 * Step 10: the marks live on the grant records themselves, and the backend
 * sends 6 matching cards at a time. The four buttons ask the backend for the
 * grants whose mark is true — across ALL grants, not just loaded ones.
 *
 * Step 29: drawn to the owner's picture — the count line at the top, the
 * search box under it, the four purple buttons, then the cards. The big
 * tab row (Grant Opportunity / CoPilot / References / Chat) and the
 * "Assigned to" select had no handlers and are gone.
 */
export default function Results() {
  const context = useContext(selectedHitContext);
  if (!context) {
    throw new Error(
      "selectedHitContext.Provider is missing. Wrap your component tree with <SelectedHitProvider>"
    );
  }

  // Step 31: which of the four buttons is pressed is remembered per user
  // ("" when none). The four flags below read from it, so everything that
  // used them works as before.
  const [pressed, setPressed] = useRemembered(useMemoryKey("view"), "");
  const isClickLiked = pressed === "liked";
  const isClickDoing = pressed === "doing";
  const isClickHidden = pressed === "hidden";
  const isClickViewed = pressed === "viewed";
  // The numbers GrantList reports back (totalMatching / totalGrants /
  // noInfoCount) so the count line can sit above the search box.
  const [counts, setCounts] = useState(null);

  // Doing / Liked / Viewed / Hidden are toggles. Clicking one shows only
  // those grants and overrides the filters on the left. Clicking the same
  // one again switches it off and the left-hand filters take over again,
  // exactly as they were — nothing on the left is ever cleared. Clicking a
  // different one switches straight to it.
  const handleClick = (name) => {
    const alreadyOn =
      (name === "doing" && isClickDoing) ||
      (name === "liked" && isClickLiked) ||
      (name === "hidden" && isClickHidden) ||
      (name === "viewed" && isClickViewed);

    setPressed(alreadyOn ? "" : name);
  };

  const activeToggle =
    (isClickDoing && "Doing") || (isClickLiked && "Liked") ||
    (isClickViewed && "Viewed") || (isClickHidden && "Hidden") || null;

  const countLine = formatCountLine(counts);

  return (

      <div className={styles.resultsRoot}>
      {/* Row 1: "24 of 1,284 grants" — shown once the numbers are known */}
      {/* Row 2: the search box (moved here from the toolbar row above the columns) */}
      <div className={styles.searchRow}>
        <SearchOnly />
      </div>

      {/* Row 3: Doing / Liked / Viewed / Hidden */}
      <div className={styles.toolbarRow}>
        <div className={styles.smallTabs}>
          <HeaderButton
            className={`${styles.smallTab} ${isClickDoing ? styles.smallTabActive : ""}`}
            onClick={() => handleClick("doing")}
            name="doing"
          >
            <span className={styles.smallTabLabel}>Doing</span>
          </HeaderButton>

          <HeaderButton
            className={`${styles.smallTab} ${isClickLiked ? styles.smallTabActive : ""}`}
            onClick={() => handleClick("liked")}
            name="liked"
          >
            <span className={styles.smallTabLabel}>Liked</span>
          </HeaderButton>

          <HeaderButton
            className={`${styles.smallTab} ${isClickViewed ? styles.smallTabActive : ""}`}
            onClick={() => handleClick("viewed")}
            name="viewed"
          >
            <span className={styles.smallTabLabel}>Viewed</span>
          </HeaderButton>

          <HeaderButton
            className={`${styles.smallTab} ${isClickHidden ? styles.smallTabActive : ""}`}
            onClick={() => handleClick("hidden")}
            name="hidden"
          >
            <span className={styles.smallTabLabel}>Hidden</span>
          </HeaderButton>
        </div>
        {countLine && (
          <div className={styles.countLine} title={countLine.title}>
            <b className={styles.countStrong}>{countLine.matching}</b>
            <span className={styles.countRest}> of {countLine.total} grants</span>
          </div>
        )}
      </div>

      {/* Row 4: the cards. The element that scrolls (and loads more) is
          GrantList's own list; this wrapper only hands it the height. */}
      <div className={styles.cardsPanel}>
        <div id="cards-scroll-area" className={styles.cardsScroller}>
          <GrantList activeToggle={activeToggle} onCounts={setCounts} />
        </div>
      </div>
    </div>
  );
}

// "24 of 1,284 grants" with a hover note about grants lacking the needed
// info. Null until both numbers are real numbers.
function formatCountLine(counts) {
  if (!counts) return null;
  const matching = Number(counts.totalMatching);
  const total = Number(counts.totalGrants);
  if (!Number.isFinite(matching) || !Number.isFinite(total)) return null;
  const noInfo = Number(counts.noInfoCount);
  const title =
    Number.isFinite(noInfo) && noInfo > 0
      ? `${noInfo.toLocaleString("en-US")} without the needed info are hidden by the active filters`
      : `${matching.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} grants match`;
  return {
    matching: matching.toLocaleString("en-US"),
    total: total.toLocaleString("en-US"),
    title,
  };
}
